import {
  CreateProjectInput,
  HourSeriesFragment,
  HourSeriesFragmentDoc,
  ProjectFragment,
  ProjectFragmentDoc,
  SimpleUserGroupFragment,
  SimpleUserGroupFragmentDoc,
  UpdateProjectInput,
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useRestoreProjectMutation,
  useUpdateProjectMutation,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'

type UseProjects = {
  isLoading: boolean

  createProject: (input: CreateProjectInput) => Promise<ProjectFragment>

  updateProject: (
    id: string,
    input: UpdateProjectInput,
  ) => Promise<ProjectFragment>

  deleteProject: (id: string) => Promise<void>

  restoreProject: (id: string) => Promise<void>
}

export function useProjects(): UseProjects {
  const [createProjectMutation, { loading: createProjectLoading }] =
    useCreateProjectMutation()
  const [updateProjectMutation, { loading: updateProjectLoading }] =
    useUpdateProjectMutation()
  const [deleteProjectMutation] = useDeleteProjectMutation()
  const [restoreProjectMutation] = useRestoreProjectMutation()
  const cacheHelpers = useCacheHelpers()

  const createProject = async (input: CreateProjectInput) => {
    const result = await createProjectMutation({
      variables: { input },
      refetchQueries: ['Projects'],
    })

    if (!result.data) {
      throw new Error('Project creation failed')
    }

    return result.data.createProject
  }

  const updateProject = async (id: string, input: UpdateProjectInput) => {
    const fragment = cacheHelpers.getFragment<ProjectFragment>(
      id,
      ProjectFragmentDoc,
    )
    if (!fragment) {
      throw new Error('Project not found in cache')
    }

    const resolveUserGroup = () => {
      if (input.userGroupId === undefined) {
        return fragment.userGroup
      }

      if (input.userGroupId === null) {
        return null
      }

      return cacheHelpers.getFragment<SimpleUserGroupFragment>(
        input.userGroupId ?? '',
        SimpleUserGroupFragmentDoc,
      )
    }

    const resolveTemperatureHourSeries = () => {
      const { temperatureHourSeriesId } = input

      if (temperatureHourSeriesId === undefined) {
        return fragment.temperatureHourSeries
      }

      if (temperatureHourSeriesId === null) {
        return null
      }

      return cacheHelpers.getFragment<HourSeriesFragment>(
        temperatureHourSeriesId,
        HourSeriesFragmentDoc,
      )
    }

    const result = await updateProjectMutation({
      variables: { id, input },
      optimisticResponse: {
        updateProject: {
          ...fragment,
          userGroup: resolveUserGroup(),
          temperatureHourSeries: resolveTemperatureHourSeries(),
          name: input.name ?? fragment.name,
          projectId: input.projectId ?? fragment.projectId,
          visibility: input.visibility ?? fragment.visibility,
          calendarYear:
            input.calendarYear === null
              ? null
              : input.calendarYear ?? fragment.calendarYear,
          licensePaid: input.licensePaid ?? fragment.licensePaid,
        },
      },
    })

    if (!result.data) {
      throw new Error('Project update failed')
    }

    return result.data.updateProject
  }

  const deleteProject = async (id: string) => {
    await deleteProjectMutation({
      variables: { id },
      optimisticResponse: {
        deleteProject: true,
      },
      refetchQueries: ['Projects'],
    })
  }

  const restoreProject = async (id: string) => {
    await restoreProjectMutation({
      variables: { id },
      optimisticResponse: {
        restoreProject: true,
      },
      refetchQueries: ['Projects'],
    })
  }

  return {
    isLoading: createProjectLoading || updateProjectLoading,
    createProject,
    updateProject,
    deleteProject,
    restoreProject,
  }
}
